import { TemplateRef } from '@angular/core';

export interface LastMessage {
  t: string;
  tpos: string | null;
  pos: ObjectCardPosType | null;
  rt: number;
}

export interface ObjectCardType {
  id: number;
  general: ObjectCardGeneralType;
  connectionSettings?: ObjectCardConnectionSettingsType;
  drivers?: ObjectCardDriverType[];
  trailers?: ObjectCardTrailerType[];
  customFields?: ObjectCardCustomFieldType[];
  sensors?: ObjectCardSensorsType[] | null;
  params?: ObjectCardParamsType[] | null;
  geozones?: ObjectCardGeozoneType[] | null;
  specifications?: ObjectCardSpecificationsType[] | null;
  maintenance?: ObjectCardMaintenanceType;
  currentUserAccess: string[];
}

/** Ключ блока карточки объекта */
export type ObjectCardBlockKey = keyof Omit<ObjectCardType, 'id' | 'currentUserAccess'>;

/**
 * Тип карточки объекта. Совпадает со значением query-параметра `type`
 * в `GET objects/{id}/?card=true&type=...` — бэкенд фильтрует блоки по настройкам этого типа.
 * - `floating` — всплывающая: попап на карте и по ховеру в списке мониторинга;
 * - `fixed` — фиксированная: раскрывается под объектом в списке мониторинга.
 */
export type ObjectCardKind = 'floating' | 'fixed';

export const OBJECT_CARD_KINDS: readonly ObjectCardKind[] = ['floating', 'fixed'];

/** Поле профиля пользователя (current_user), в котором лежат настройки карточки данного типа */
export const OBJECT_CARD_SETTINGS_FIELD: Record<ObjectCardKind, ObjectCardSettingsField> = {
  floating: 'objectCardSettings',
  fixed: 'objectCardFixed',
};

export type ObjectCardSettingsField = 'objectCardSettings' | 'objectCardFixed';

/** Какие блоки карточки раскрыты (аккордеоны) */
export type ObjectCardExpandedState = Partial<Record<ObjectCardBlockKey, boolean>>;

/** Настраиваемые поля блока general. Порядок отображения фиксирован и задан этим массивом. */
export const OBJECT_CARD_GENERAL_FIELDS = [
  'icon',
  'speed',
  'satellites',
  'address',
  't',
  'tpos',
] as const;

export type ObjectCardGeneralFieldKey = (typeof OBJECT_CARD_GENERAL_FIELDS)[number];

/** Флаги видимости полей блока general */
export type ObjectCardGeneralContentType = Record<ObjectCardGeneralFieldKey, boolean>;

export interface ObjectCardBlockSettingsType {
  /** Позиция блока в карточке. У general всегда 0. */
  index: number;
  /** Список видимых полей блока. Заполнен только у general, у остальных блоков пуст. */
  content: string[];
}

/** Настройки карточки объекта из профиля текущего пользователя (current_user.objectCardSettings) */
export type ObjectCardSettingsType = Partial<
  Record<ObjectCardBlockKey, ObjectCardBlockSettingsType>
>;

/**
 * @deprecated Старый плоский формат настроек (`{ general: 0, sensors: 1, ... }`).
 * Оставлен для чтения ответов бэкенда, не обновлённого до AXENTA-2594.
 * Использовать {@link ObjectCardSettingsType}.
 */
export type OrderByCurrentUserSettingsType = Omit<
  Record<keyof ObjectCardType, number>,
  'id' | 'currentUserAccess'
>;
export type TemplateByCurrentUserSettingsType = Omit<
  Record<keyof ObjectCardType, TemplateRef<any> | null>,
  'id' | 'currentUserAccess'
>;

export interface ObjectCardGeneralType {
  iconId: number;
  objectIconColor: string;
  deviceTypeId: number;
  name: string;
  connectedStatus: boolean;
  lastMessage: LastMessage | null;
  address: string | null;
}

export interface ObjectCardConnectionSettingsType {
  deviceTypeName: string;
  uniqueId: string;
  phoneNumber: string[];
}

export interface ObjectCardDriverType {
  id: number;
  name: string;
  phoneNumber: string | null;
}

export interface ObjectCardTrailerType {
  id: number;
  name: string;
}

export interface ObjectCardCustomFieldType {
  name: string;
  value: string | null;
  isAdminField: boolean;
}

// export interface ObjectCardSensorsType {
//   [key: string]: string | null;
// }
export interface ObjectCardSensorsType {
  name: string;
  value: string | null;
  sortIndex: number;
}

export interface ObjectCardParamsType {
  name: string;
  value: string | number | null;
}

export interface ObjectCardSpecificationsType {
  name: string;
  value: string;
}

export interface ObjectCardGeozoneType {
  id: number;
  name: string;
}

export interface ObjectCardMaintenanceType {
  mileageSource: string | null;
  engineHoursSource: string | null;
  list: {
    name: string;
    description: string;
    executionsCount: number;
    settings: {
      mileage: {
        enabled: boolean;
        interval: number | null;
        last: number | null;
        current: number | null;
      };
      engineHours: {
        enabled: boolean;
        interval: number | null;
        last: number | null;
        current: number | null;
      };
      days: {
        enabled: boolean;
        interval: number | null;
        last: number | null;
      };
    };
  }[];
}

export interface ObjectCardLastMessageType {
  t: string;
  tpos: string | null;
  pos: ObjectCardPosType | null;
  rt: number;
}

export interface ObjectCardPosType {
  c?: number | null;
  s?: string | number | null;
  x?: number | null;
  y?: number | null;
  z?: number | null;
  sc?: number | null;
}
